import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type ReactNode } from 'react'
import { Button, Chip, Description, Input, Label, SearchField, TextField, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { useTextLanguages } from '../lib/i18n/useCustomerLanguages'
import { MediaPlayer } from '../layout/MediaPlayer'
import { objectFitOf } from '../layout/playlist'
import { useCanvasScale } from '../layout/render'
import type { LayoutElement, MediaFit } from '../layout/types'
import { api, type PlaylistEntry, type PlaylistSummary, type SignageMedia, type TickerLine } from '../lib/api'
import { FOCUSABLE, trapStep } from '../lib/focusTrap'
import {
  afterRemove,
  centreSize,
  draftKey,
  insertEntries,
  invalidItems,
  itemSeconds,
  moveByKey,
  playItems,
  reducedMotion,
  secondsValid,
  shownTotal,
  stageCanvas,
  stripSeconds,
  timeline,
  toSend,
  typedEntry,
  usageOf,
  type DraftEntry,
  type Said,
  type StageShape,
} from '../lib/playlistView'
import { LIBRARY_ACCEPT, MAX_ENTRIES, MAX_SECONDS, MAX_TICKER_LINES, MIN_SECONDS, clockOf, filmLength, moveEntry, nonBlankTicker, playlistProblems } from '../lib/signageEdit'
import { mosaicOf, viewMedia, viewPlaylists, type KindFilter } from '../lib/signageViews'
import { explain } from '../lib/staffErrors'
import { formatMoment, type StaffKey } from '../lib/staffText'
import { Notice } from '../admin/settingsKit'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { showToast } from '../ui/toast'
import type { SignageData } from './signageData'
import { ConfirmModal, Empty, MoreMenu, PickOne, TileSkeletons, UploadButton } from './signageKit'
import { DropSheet, filmHover, Thumb, UploadList, useFileDrop, useLibraryUpload } from './signageLibrary'

type Draft = { id?: string; name: string; version: number; entries: DraftEntry[]; ticker: TickerLine[] }

const FITS: ReadonlyArray<[MediaFit, StaffKey]> = [
  ['fill', 'signage.fit.fill'],
  ['fit', 'signage.fit.fit'],
  ['centre', 'signage.fit.centre'],
]

const SHAPES: ReadonlyArray<[StageShape, string]> = [
  ['wide', '16:9'],
  ['tall', '9:16'],
]

/** Drag payloads inside the editor: a library file, or an item being moved. */
const DRAG_MEDIA = 'application/x-qms-media'
const DRAG_ITEM = 'application/x-qms-item'

const keyOf = (d: Draft) => draftKey(d.name, d.entries, d.ticker)

/** Saved playlists as cards, and one being edited; the address says which (#playlists/<id>). */
export function PlaylistsTab({
  data,
  openId,
  seed,
  onOpen,
}: {
  data: SignageData
  /** The playlist the address names; 'new' for a new one. */
  openId: string
  /** Library files a new playlist starts with. */
  seed: SignageMedia[] | null
  onOpen: (id: string) => void
}) {
  const { say, lang } = useStaffText()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [find, setFind] = useState('')
  const [deleting, setDeleting] = useState<PlaylistSummary | null>(null)

  // The address leads: a new id loads that playlist, none closes the editor.
  useEffect(() => {
    if (!openId) {
      setDraft(null)
      return
    }
    if (openId === 'new') {
      const fresh: Draft = { name: '', version: 0, entries: insertEntries([], seed ?? [], 0), ticker: [] }
      setDraft(fresh)
      setSaved(keyOf({ ...fresh, entries: [] }))
      return
    }
    if (draft?.id === openId) return
    let live = true
    setNotice('')
    api
      .playlist(openId)
      .then(({ playlist }) => {
        if (!live) return
        const d: Draft = { id: playlist.id, name: playlist.name, version: playlist.version, entries: playlist.entries, ticker: playlist.ticker }
        setDraft(d)
        setSaved(keyOf(d))
      })
      .catch((err) => live && setNotice(explain(err, lang, 'common.unreachable')))
    return () => {
      live = false
    }
    // draft is read, not followed: a save that names the playlist must not load it again.
  }, [openId, seed, lang])

  const save = async () => {
    if (!draft) return
    const problems = playlistProblems(draft.name, draft.entries, draft.ticker, data.media)
    if (problems.length > 0) {
      setNotice(problems.map((k) => say(k)).join(' '))
      return
    }
    setBusy(true)
    setNotice('')
    try {
      const body = { name: draft.name.trim(), entries: toSend(draft.entries), ticker: nonBlankTicker(draft.ticker) }
      const { playlist } = draft.id ? await api.savePlaylist(draft.id, { ...body, version: draft.version }) : await api.createPlaylist(body)
      const d: Draft = { id: playlist.id, name: playlist.name, version: playlist.version, entries: playlist.entries, ticker: playlist.ticker }
      setDraft(d)
      setSaved(keyOf(d))
      showToast(say('common.saved'))
      if (!draft.id) onOpen(playlist.id)
      void data.reload('playlists', 'media')
    } catch (err) {
      setNotice(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    setBusy(true)
    setNotice('')
    try {
      await api.deletePlaylist(id)
      setDeleting(null)
      showToast(say('signage.deleted'))
      if (openId) onOpen('')
      await data.reload('playlists', 'media', 'campaigns')
    } catch (err) {
      setDeleting(null)
      setNotice(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const duplicate = async (id: string) => {
    setBusy(true)
    setNotice('')
    try {
      const { playlist: p } = await api.playlist(id)
      const { playlist } = await api.createPlaylist({ name: say('signage.copyName', { name: p.name }).slice(0, 80), entries: p.entries, ticker: p.ticker })
      showToast(say('signage.duplicated'))
      await data.reload('playlists', 'media')
      onOpen(playlist.id)
    } catch (err) {
      setNotice(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const newButton = (
    <Button variant="primary" size="sm" onPress={() => onOpen('new')}>
      <Icon name="plus" size={16} /> {say('signage.newPlaylist')}
    </Button>
  )

  if (openId) {
    return (
      <>
        {notice && <Notice>{notice}</Notice>}
        {draft ? (
          <PlaylistEditor
            draft={draft}
            dirty={keyOf(draft) !== saved}
            data={data}
            busy={busy}
            onChange={(update) => setDraft((d) => (d ? update(d) : d))}
            onSave={() => void save()}
            onBack={() => {
              setNotice('')
              onOpen('')
            }}
            onDelete={draft.id ? () => setDeleting(data.playlists.find((p) => p.id === draft.id) ?? ({ id: draft.id, name: draft.name } as PlaylistSummary)) : undefined}
            onDuplicate={draft.id ? () => void duplicate(draft.id!) : undefined}
          />
        ) : (
          !notice && <TileSkeletons n={3} />
        )}
        {deleting && <DeletePlaylist playlist={deleting} busy={busy} onClose={() => setDeleting(null)} onConfirm={() => void remove(deleting.id)} />}
      </>
    )
  }

  const shown = viewPlaylists(data.playlists, find)
  return (
    <section className="signage-lists" aria-label={say('signage.tab.playlists')}>
      {data.playlists.length > 0 && (
        <div className="signage-bar">
          <SearchField className="signage-find" aria-label={say('signage.findPlaylist')} value={find} onChange={setFind} variant="secondary">
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={say('signage.findPlaylist')} />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <span className="signage-count">{say('signage.nPlaylists', { n: data.playlists.length })}</span>
          <span className="signage-bar__gap" />
          {newButton}
        </div>
      )}
      {(notice || data.faults.playlists) && <Notice>{notice || data.faults.playlists}</Notice>}
      {!data.loaded.playlists ? (
        <TileSkeletons n={6} className="signage-cards" />
      ) : data.playlists.length === 0 ? (
        <div className="signage-dropzone">
          <Empty icon="play" title={say('signage.playlists.empty')} action={newButton} />
        </div>
      ) : shown.length === 0 ? (
        <Empty icon="search" title={say('signage.noMatch', { q: find.trim() })} />
      ) : (
        <ul className="signage-cards">
          {shown.map((p) => (
            <li key={p.id}>
              <PlaylistCard
                playlist={p}
                library={data.media}
                onOpen={() => onOpen(p.id)}
                onAction={(id) => {
                  if (id === 'open') onOpen(p.id)
                  if (id === 'duplicate') void duplicate(p.id)
                  if (id === 'delete') setDeleting(p)
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {deleting && <DeletePlaylist playlist={deleting} busy={busy} onClose={() => setDeleting(null)} onConfirm={() => void remove(deleting.id)} />}
    </section>
  )
}

function DeletePlaylist({ playlist, busy, onClose, onConfirm }: { playlist: Pick<PlaylistSummary, 'name' | 'campaigns'>; busy: boolean; onClose: () => void; onConfirm: () => void }) {
  const { say } = useStaffText()
  return (
    <ConfirmModal title={say('signage.deleteOne', { name: playlist.name })} confirm={say('common.delete')} busy={busy} onClose={onClose} onConfirm={onConfirm}>
      {(playlist.campaigns?.length ?? 0) > 0 && <p className="signage-confirm__line">{say('signage.inCampaigns', { n: playlist.campaigns.length })}</p>}
    </ConfirmModal>
  )
}

function useSaid() {
  const { say } = useStaffText()
  return (s: Said) => say(s.key, s.n === undefined ? undefined : { n: s.n })
}

/** A phrase, or a refused time as it was typed. */
function useShown() {
  const said = useSaid()
  return (s: Said | { raw: string }) => ('raw' in s ? s.raw : said(s))
}

/** A playlist's first four files as a mosaic; one file fills the face. */
function Mosaic({ files }: { files: SignageMedia[] }) {
  if (files.length === 0) {
    return (
      <span className="signage-mosaic signage-mosaic--none">
        <Icon name="play" size={26} />
      </span>
    )
  }
  return (
    <span className="signage-mosaic" data-n={Math.min(files.length, 4)}>
      {files.map((m) => (
        <Thumb key={m.id} media={m} className="signage-face" />
      ))}
    </span>
  )
}

function PlaylistCard({
  playlist: p,
  library,
  onOpen,
  onAction,
}: {
  playlist: PlaylistSummary
  library: SignageMedia[]
  onOpen: () => void
  onAction: (id: string) => void
}) {
  const { say, lang } = useStaffText()
  const said = useSaid()
  const usedBy = [...p.campaigns, ...p.designs].join(', ')
  const used = p.campaigns.length + p.designs.length > 0
  return (
    <article className="signage-card">
      <button type="button" className="signage-card__open" aria-label={p.name} onClick={onOpen} {...filmHover}>
        <span className="signage-card__cover">
          <Mosaic files={mosaicOf(p, library)} />
          {p.items > 0 && <span className="signage-badge">{clockOf(p.totalSeconds)}</span>}
        </span>
      </button>
      <div className="signage-card__body">
        <span className="signage-card__name" title={p.name}>
          {p.name}
        </span>
        <span className="signage-card__meta">
          {say('signage.nItems', { n: p.items })}
          {p.ticker.length > 0 && ` · ${say('signage.nLines', { n: p.ticker.length })}`} · {say('signage.edited', { date: formatMoment(p.updatedAt, lang, { date: true }) })}
        </span>
        <span className="signage-card__used" data-used={used || undefined} title={usedBy || undefined}>
          <i className="signage-dot" aria-hidden="true" />
          {usageOf(p).map(said).join(' · ')}
        </span>
      </div>
      <MoreMenu
        className="signage-tile__menu"
        label={say('signage.actions', { name: p.name })}
        onAction={onAction}
        actions={[
          { id: 'open', label: say('signage.edit'), icon: 'edit' },
          { id: 'duplicate', label: say('signage.duplicateIt'), icon: 'copy' },
          { id: 'delete', label: say('common.delete'), icon: 'trash', danger: true },
        ]}
      />
    </article>
  )
}

function PlaylistEditor({
  draft,
  dirty,
  data,
  busy,
  onChange,
  onSave,
  onBack,
  onDelete,
  onDuplicate,
}: {
  draft: Draft
  dirty: boolean
  data: SignageData
  busy: boolean
  onChange: (update: (d: Draft) => Draft) => void
  onSave: () => void
  onBack: () => void
  onDelete?: () => void
  onDuplicate?: () => void
}) {
  const { say } = useStaffText()
  const library = data.media
  // The ticker stores az, ru and en only (signage.TickerLine); the others wait for its own migration.
  const offeredTicker = useTextLanguages().filter(([c]) => c === 'az' || c === 'ru' || c === 'en')
  const TEXT_LANGUAGES: Array<[string, string]> = offeredTicker.length > 0 ? offeredTicker : [['en', 'English']]
  const shown = useShown()
  const [selected, setSelected] = useState(draft.entries.length > 0 ? 0 : -1)
  const [shape, setShape] = useState<StageShape>('wide')
  const [playing, setPlaying] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [dragging, setDragging] = useState<number | null>(null)
  const [dropAt, setDropAt] = useState<number | null>(null)
  const [leaving, setLeaving] = useState(false)
  const strip = useRef<Array<HTMLButtonElement | null>>([])
  const refocus = useRef(false)
  const byId = useMemo(() => new Map(library.map((m) => [m.id, m])), [library])
  const set = (patch: Partial<Draft>) => onChange((d) => ({ ...d, ...patch }))
  const setEntry = (i: number, e: DraftEntry) => onChange((d) => ({ ...d, entries: d.entries.map((x, j) => (j === i ? e : x)) }))
  const setLine = (i: number, line: TickerLine) => onChange((d) => ({ ...d, ticker: d.ticker.map((l, j) => (j === i ? line : l)) }))
  const entry = draft.entries[selected]
  const media = entry ? byId.get(entry.mediaItemId) : undefined
  const bad = invalidItems(draft.entries, library)
  const total = shownTotal(draft.entries, library)
  const bars = timeline(draft.entries, library)
  const room = MAX_ENTRIES - draft.entries.length

  // Files uploaded from the editor's library go on the end of the list.
  const up = useLibraryUpload((m) => {
    data.addMedia(m)
    onChange((d) => ({ ...d, entries: insertEntries(d.entries, [m], d.entries.length) }))
    setSelected((s) => (s < 0 ? 0 : s))
  })
  const drop = useFileDrop((files) => void up.start(files), up.sending)

  // An item moved by the keyboard keeps the focus as it moves.
  useEffect(() => {
    if (!refocus.current) return
    refocus.current = false
    strip.current[selected]?.focus()
  }, [selected, draft.entries])

  // The stage's own preview: each item for its time, then the next, round again.
  const seconds = entry ? itemSeconds(entry, media) : 0
  useEffect(() => {
    if (!previewing || !entry) return
    const began = Date.now()
    setElapsed(0)
    const tick = window.setInterval(() => setElapsed((Date.now() - began) / 1000), 200)
    const next = window.setTimeout(() => setSelected((s) => (s + 1) % Math.max(draft.entries.length, 1)), seconds * 1000)
    return () => {
      window.clearInterval(tick)
      window.clearTimeout(next)
    }
  }, [previewing, selected, seconds, entry, draft.entries.length])
  useEffect(() => {
    if (draft.entries.length === 0) setPreviewing(false)
  }, [draft.entries.length])

  const remove = (i: number) => {
    setSelected(afterRemove(draft.entries.length, i, selected))
    set({ entries: draft.entries.filter((_, j) => j !== i) })
  }
  const move = (from: number, to: number) => {
    set({ entries: moveEntry([...draft.entries], from, to) })
    setSelected(to)
  }
  const addAt = (files: SignageMedia[], at: number) => {
    if (files.length === 0 || room <= 0) return
    onChange((d) => ({ ...d, entries: insertEntries(d.entries, files, at) }))
    setSelected(Math.min(at, draft.entries.length))
  }
  // A drop on the strip: a library file goes in there, an item moves there.
  const dropOn = (e: DragEvent, at: number) => {
    e.preventDefault()
    setDropAt(null)
    const mediaId = e.dataTransfer.getData(DRAG_MEDIA)
    if (mediaId) {
      const m = byId.get(mediaId)
      if (m) addAt([m], at)
    } else if (dragging !== null && dragging !== at) {
      move(dragging, at > dragging ? at - 1 : at)
    }
    setDragging(null)
  }
  const overStrip = (e: DragEvent, at: number) => {
    if (!e.dataTransfer.types.includes(DRAG_MEDIA) && !e.dataTransfer.types.includes(DRAG_ITEM)) return
    e.preventDefault()
    setDropAt(at)
  }

  const position = bars[selected]
  const playhead = position && previewing ? Math.min(position.start + (Math.min(elapsed, position.seconds) / position.seconds) * position.share, 1) : null

  return (
    <div className="signage-ed">
      <header className="signage-ed__head">
        <Button isIconOnly variant="ghost" size="sm" aria-label={say('common.back')} onPress={() => (dirty ? setLeaving(true) : onBack())}>
          <Icon name="left" size={18} />
        </Button>
        <Input
          variant="secondary"
          className="signage-ed__name"
          aria-label={say('signage.name')}
          placeholder={say('signage.namePlaylist')}
          value={draft.name}
          maxLength={80}
          autoFocus={!draft.id && draft.name === ''}
          onChange={(e) => set({ name: e.target.value })}
        />
        <Chip size="sm" variant="soft" color={dirty ? 'warning' : 'success'} className="signage-ed__state">
          {dirty ? say('signage.unsaved') : say('signage.savedState')}
        </Chip>
        <span className="signage-ed__meta">
          {say('signage.nItems', { n: draft.entries.length })} · {total === null ? '—' : clockOf(total)}
        </span>
        <span className="signage-ed__acts">
          <Button variant="secondary" size="sm" isDisabled={playItems(draft.entries, library).length === 0} onPress={() => setPlaying(true)}>
            <Icon name="arrowUpRight" size={14} /> {say('signage.fullScreen')}
          </Button>
          <Button variant="primary" size="sm" isPending={busy} isDisabled={busy || bad.length > 0 || (!dirty && !!draft.id)} onPress={onSave}>
            {say('common.save')}
          </Button>
          {(onDuplicate || onDelete) && (
            <MoreMenu
              label={say('signage.actions', { name: draft.name })}
              onAction={(id) => {
                if (id === 'duplicate') onDuplicate?.()
                if (id === 'delete') onDelete?.()
              }}
              actions={[
                ...(onDuplicate ? [{ id: 'duplicate', label: say('signage.duplicateIt'), icon: 'copy' as const, disabled: dirty }] : []),
                ...(onDelete ? [{ id: 'delete', label: say('common.delete'), icon: 'trash' as const, danger: true }] : []),
              ]}
            />
          )}
        </span>
      </header>

      <div className="signage-ed__panes">
        <aside className="signage-ed__lib signage-droptarget" aria-label={say('signage.tab.library')} {...drop.props}>
          <EditorLibrary
            library={library}
            room={room}
            onAdd={(m) => addAt([m], draft.entries.length)}
            upload={<UploadButton accept={LIBRARY_ACCEPT} label={say('signage.upload')} disabled={up.sending} onFiles={(files) => void up.start(files)} />}
          />
          <UploadList rows={up.uploads} sending={up.sending} onClear={up.clear} />
          {drop.dropping && <DropSheet />}
        </aside>

        <section className="signage-ed__centre" aria-label={say('signage.preview')}>
          <div className="signage-ed__stagebar">
            <ToggleButtonGroup
              className="signage-seg"
              size="sm"
              selectionMode="single"
              disallowEmptySelection
              aria-label={say('signage.shape')}
              selectedKeys={[shape]}
              onSelectionChange={(keys) => {
                const next = [...keys][0]
                if (next === 'wide' || next === 'tall') setShape(next)
              }}
            >
              {SHAPES.map(([value, label]) => (
                <ToggleButton key={value} id={value}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <span className="signage-ed__transport">
              <Button isIconOnly size="sm" variant="ghost" aria-label={say('signage.prev')} isDisabled={draft.entries.length < 2} onPress={() => setSelected((s) => (s - 1 + draft.entries.length) % draft.entries.length)}>
                <Icon name="left" size={16} />
              </Button>
              <Button
                isIconOnly
                size="sm"
                variant="secondary"
                aria-label={previewing ? say('signage.pause') : say('signage.play')}
                isDisabled={draft.entries.length === 0}
                onPress={() => {
                  if (selected < 0) setSelected(0)
                  setPreviewing(!previewing)
                }}
              >
                <Icon name={previewing ? 'pause' : 'play'} size={16} />
              </Button>
              <Button isIconOnly size="sm" variant="ghost" aria-label={say('signage.next')} isDisabled={draft.entries.length < 2} onPress={() => setSelected((s) => (s + 1) % draft.entries.length)}>
                <Icon name="right" size={16} />
              </Button>
              <span className="signage-ed__pos">{draft.entries.length > 0 && selected >= 0 ? `${selected + 1} / ${draft.entries.length}` : ''}</span>
            </span>
          </div>
          <Stage entry={entry} media={media} shape={shape} hold={playing} />

          <div className="signage-timeline">
            {bars.length > 0 && (
              <div className="signage-timeline__bar" aria-hidden="true">
                {bars.map((b, i) => (
                  <span
                    key={i}
                    className="signage-timeline__seg"
                    data-on={i === selected || undefined}
                    data-bad={bad.includes(i) || undefined}
                    style={{ left: `${b.start * 100}%`, width: `${b.share * 100}%` }}
                    onClick={() => setSelected(i)}
                  />
                ))}
                {playhead !== null && <span className="signage-timeline__head" style={{ left: `${playhead * 100}%` }} />}
              </div>
            )}
            <ol className="signage-strip" aria-label={say('signage.items')} onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setDropAt(null)}>
              {draft.entries.map((e, i) => {
                const m = byId.get(e.mediaItemId)
                return (
                  <li
                    key={`${i}:${e.mediaItemId}`}
                    className="signage-strip__cell"
                    data-dragging={dragging === i || undefined}
                    data-drop={dropAt === i || undefined}
                    onDragOver={(ev) => overStrip(ev, i)}
                    onDrop={(ev) => dropOn(ev, i)}
                  >
                    <button
                      type="button"
                      ref={(el) => {
                        strip.current[i] = el
                      }}
                      className="signage-strip__item"
                      aria-pressed={selected === i}
                      aria-invalid={bad.includes(i) || undefined}
                      aria-label={`${i + 1}. ${m?.name ?? e.mediaItemId}`}
                      draggable
                      onDragStart={(ev) => {
                        ev.dataTransfer.setData(DRAG_ITEM, String(i))
                        ev.dataTransfer.effectAllowed = 'move'
                        setDragging(i)
                      }}
                      onDragEnd={() => {
                        setDragging(null)
                        setDropAt(null)
                      }}
                      onClick={() => setSelected(i)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Delete' || ev.key === 'Backspace') {
                          ev.preventDefault()
                          remove(i)
                          return
                        }
                        const next = moveByKey(draft.entries, i, ev.key)
                        if (!next) return
                        ev.preventDefault()
                        refocus.current = true
                        set({ entries: next.entries })
                        setSelected(next.index)
                      }}
                    >
                      <span className="signage-strip__face">
                        {m ? <Thumb media={m} className="signage-face" /> : <Icon name="alert" size={16} />}
                        <span className="signage-strip__n">{i + 1}</span>
                      </span>
                      <span className="signage-strip__foot">
                        <span className="signage-strip__name">{m?.name ?? '—'}</span>
                        <span className="signage-strip__secs">{shown(stripSeconds(e, m))}</span>
                      </span>
                    </button>
                    <Button isIconOnly size="sm" variant="secondary" className="signage-strip__x" aria-label={`${say('common.remove')}: ${m?.name ?? ''}`} onPress={() => remove(i)}>
                      <Icon name="close" size={12} />
                    </Button>
                  </li>
                )
              })}
              {room > 0 && (
                <li
                  className="signage-strip__cell signage-strip__cell--end"
                  data-drop={dropAt === draft.entries.length || undefined}
                  onDragOver={(ev) => overStrip(ev, draft.entries.length)}
                  onDrop={(ev) => dropOn(ev, draft.entries.length)}
                >
                  <span className="signage-strip__add">
                    <Icon name="drag" size={16} />
                    <span>{draft.entries.length === 0 ? say('signage.dragFirst') : say('signage.dragMore')}</span>
                  </span>
                </li>
              )}
            </ol>
          </div>
        </section>

        <aside className="signage-ed__props" aria-label={say('signage.item')}>
          {entry ? (
            <>
              <div className="signage-props__head">
                {media && <Thumb media={media} className="signage-thumb" />}
                <span className="signage-props__name" title={media?.name}>
                  {media?.name ?? entry.mediaItemId}
                </span>
                <span className="signage-muted">
                  {say(media?.kind === 'video' ? 'signage.film' : 'signage.picture')}
                  {media && filmLength(media) && ` · ${filmLength(media)}`} · {say('signage.itemOf', { n: selected + 1, of: draft.entries.length })}
                </span>
              </div>
              <SecondsField key={`${selected}:${entry.mediaItemId}`} entry={entry} media={media} onChange={(next) => setEntry(selected, next)} />
              <div className="signage-field">
                <span className="signage-field__label">{say('signage.fit')}</span>
                <ToggleButtonGroup
                  className="signage-seg signage-seg--fill"
                  size="sm"
                  selectionMode="single"
                  disallowEmptySelection
                  aria-label={say('signage.fit')}
                  selectedKeys={[entry.fit]}
                  onSelectionChange={(keys) => {
                    const fit = [...keys][0] as MediaFit | undefined
                    if (fit) setEntry(selected, { ...entry, fit })
                  }}
                >
                  {FITS.map(([value, key]) => (
                    <ToggleButton key={value} id={value}>
                      {say(key)}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>
              <div className="signage-props__acts">
                <Button size="sm" variant="ghost" aria-label={say('signage.earlier')} isDisabled={selected === 0} onPress={() => move(selected, selected - 1)}>
                  <Icon name="left" size={14} /> {say('signage.earlier')}
                </Button>
                <Button size="sm" variant="ghost" aria-label={say('signage.later')} isDisabled={selected >= draft.entries.length - 1} onPress={() => move(selected, selected + 1)}>
                  {say('signage.later')} <Icon name="right" size={14} />
                </Button>
              </div>
              <Button size="sm" variant="danger-soft" className="signage-props__remove" onPress={() => remove(selected)}>
                <Icon name="trash" size={14} /> {say('common.remove')}
              </Button>
            </>
          ) : (
            <span className="signage-props__none">
              <Icon name="info" size={18} />
              {say('signage.pickItem')}
            </span>
          )}
        </aside>
      </div>

      <section className="signage-ed__ticker" aria-label={say('signage.ticker')}>
        <div className="signage-ed__tickerhead">
          <h2 className="signage-h2">{say('signage.ticker')}</h2>
          <span className="signage-count">{say('signage.nLines', { n: draft.ticker.length })}</span>
          <span className="signage-bar__gap" />
          {draft.ticker.length < MAX_TICKER_LINES && (
            <Button variant="secondary" size="sm" onPress={() => set({ ticker: [...draft.ticker, { az: '', ru: '', en: '' }] })}>
              <Icon name="plus" size={14} /> {say('signage.addLine')}
            </Button>
          )}
        </div>
        {draft.ticker.length > 0 && (
          <ol className="signage-ticker" style={{ ['--ticker-langs' as string]: TEXT_LANGUAGES.length }}>
            {TEXT_LANGUAGES.length > 1 && (
              <li className="signage-ticker__row signage-ticker__row--head" aria-hidden="true">
                <span />
                {TEXT_LANGUAGES.map(([code, label]) => (
                  <span key={code}>{label}</span>
                ))}
                <span />
              </li>
            )}
            {draft.ticker.map((line, i) => (
              <li key={i} className="signage-ticker__row">
                <span className="signage-ticker__n">{i + 1}</span>
                {TEXT_LANGUAGES.map(([code, label]) => (
                  <Input
                    key={code}
                    variant="secondary"
                    aria-label={`${say('signage.line', { n: i + 1 })} · ${label}`}
                    placeholder={TEXT_LANGUAGES.length > 1 ? code : say('signage.lineText')}
                    maxLength={200}
                    value={line[code as keyof TickerLine]}
                    onChange={(ev) => setLine(i, { ...line, [code]: ev.target.value })}
                  />
                ))}
                <span className="signage-ticker__acts">
                  <Button isIconOnly variant="ghost" size="sm" aria-label={say('signage.earlier')} isDisabled={i === 0} onPress={() => set({ ticker: moveEntry([...draft.ticker], i, i - 1) })}>
                    <Icon name="up" size={14} />
                  </Button>
                  <Button isIconOnly variant="ghost" size="sm" aria-label={say('signage.later')} isDisabled={i === draft.ticker.length - 1} onPress={() => set({ ticker: moveEntry([...draft.ticker], i, i + 1) })}>
                    <Icon name="down" size={14} />
                  </Button>
                  <Button isIconOnly variant="ghost" size="sm" aria-label={say('common.remove')} onPress={() => set({ ticker: draft.ticker.filter((_, j) => j !== i) })}>
                    <Icon name="trash" size={14} />
                  </Button>
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {playing && <PlayAll entries={draft.entries} library={library} shape={shape} onClose={() => setPlaying(false)} />}
      {leaving && (
        <ConfirmModal title={say('signage.discardAsk')} confirm={say('signage.discard')} onClose={() => setLeaving(false)} onConfirm={onBack} />
      )}
    </div>
  )
}

/** The editor's left pane: the library to drag from or click to add. */
function EditorLibrary({ library, room, onAdd, upload }: { library: SignageMedia[]; room: number; onAdd: (m: SignageMedia) => void; upload: ReactNode }) {
  const { say } = useStaffText()
  const [find, setFind] = useState('')
  const [kind, setKind] = useState<KindFilter>('all')
  const shown = viewMedia(library, find, kind, 'newest')
  return (
    <>
      <div className="signage-ed__libhead">
        <h2 className="signage-h2">{say('signage.tab.library')}</h2>
        {upload}
      </div>
      <SearchField className="signage-ed__libfind" aria-label={say('signage.findFile')} value={find} onChange={setFind} variant="secondary">
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder={say('signage.findFile')} />
          <SearchField.ClearButton />
        </SearchField.Group>
      </SearchField>
      <ToggleButtonGroup
        className="signage-seg signage-seg--fill"
        size="sm"
        selectionMode="single"
        disallowEmptySelection
        aria-label={say('signage.kind')}
        selectedKeys={[kind]}
        onSelectionChange={(keys) => setKind(([...keys][0] as KindFilter) ?? 'all')}
      >
        <ToggleButton id="all">{say('signage.kind.all')}</ToggleButton>
        <ToggleButton id="image">{say('signage.kind.pictures')}</ToggleButton>
        <ToggleButton id="video">{say('signage.kind.films')}</ToggleButton>
      </ToggleButtonGroup>
      {library.length === 0 ? (
        <Empty icon="upload" title={say('signage.library.empty')} line={say('signage.formats')} />
      ) : shown.length === 0 ? (
        <p className="signage-muted signage-ed__libnone">{say('signage.noMatch', { q: find.trim() })}</p>
      ) : (
        <ul className="signage-ed__libgrid">
          {shown.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                className="signage-mini"
                draggable={room > 0}
                disabled={room <= 0}
                aria-label={`${say('common.add')}: ${m.name}`}
                title={m.name}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_MEDIA, m.id)
                  e.dataTransfer.effectAllowed = 'copy'
                }}
                onClick={() => onAdd(m)}
                {...filmHover}
              >
                <span className="signage-mini__face">
                  <Thumb media={m} className="signage-face" />
                  {filmLength(m) && <span className="signage-badge">{filmLength(m)}</span>}
                  <span className="signage-mini__add" aria-hidden="true">
                    <Icon name="plus" size={16} />
                  </span>
                </span>
                <span className="signage-mini__name">{m.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/** A picture's seconds, or a film's: to its end or a number. Same limits as the server. */
function SecondsField({ entry, media, onChange }: { entry: DraftEntry; media?: SignageMedia; onChange: (next: DraftEntry) => void }) {
  const { say } = useStaffText()
  const kind = media?.kind ?? 'image'
  const [text, setText] = useState(entry.typed ?? (entry.seconds > 0 ? String(entry.seconds) : ''))
  const { typed: _, ...kept } = entry
  const toEnd = kind === 'video' && entry.seconds === 0
  const bad = !secondsValid(entry.seconds, kind)
  const box = (
    <TextField
      className="signage-secs"
      type="number"
      isInvalid={bad}
      aria-label={kind === 'image' ? undefined : say('signage.seconds')}
      value={text}
      onChange={(next) => {
        setText(next)
        onChange(typedEntry(entry, next, kind))
      }}
    >
      {kind === 'image' && <Label>{say('signage.seconds')}</Label>}
      <Input variant="secondary" inputMode="numeric" min={MIN_SECONDS} max={MAX_SECONDS} placeholder={kind === 'image' ? '10' : undefined} />
      <Description className="signage-range" data-bad={bad || undefined}>
        {say('signage.secondsRange', { min: MIN_SECONDS, max: MAX_SECONDS })}
      </Description>
    </TextField>
  )
  if (kind === 'image') return box
  const length = Math.round((media?.durationMs ?? 0) / 1000)
  return (
    <div className="signage-duration">
      <PickOne
        label={say('signage.duration')}
        value={toEnd ? 'end' : 'secs'}
        onChange={(v) => {
          if (v === 'end') {
            setText('')
            onChange({ ...kept, seconds: 0 })
            return
          }
          const n = length >= MIN_SECONDS && length <= MAX_SECONDS ? length : 10
          setText(String(n))
          onChange({ ...kept, seconds: n })
        }}
        options={[
          { value: 'end', label: say('signage.toEnd') },
          { value: 'secs', label: say('signage.seconds') },
        ]}
      />
      {!toEnd && box}
    </div>
  )
}

/** The selected item at the screen's shape, filled as the TV fills it. */
function Stage({ entry, media, shape, hold }: { entry?: PlaylistEntry; media?: SignageMedia; shape: StageShape; hold: boolean }) {
  const { say } = useStaffText()
  const { width, height } = stageCanvas(shape)
  const film = useRef<HTMLVideoElement>(null)
  // Plays by itself unless less motion is asked for; held while Play runs over it.
  useEffect(() => {
    const v = film.current
    if (!v) return
    if (hold || reducedMotion()) v.pause()
    else void v.play().catch(() => undefined)
  }, [hold, media?.id])
  const { ref, scale } = useCanvasScale(width, height)
  const [natural, setNatural] = useState<{ width: number; height: number }>()
  useEffect(() => setNatural(undefined), [media?.id])
  const fit = objectFitOf(entry?.fit)
  const centred = fit === 'none' ? centreSize(natural, scale) : undefined
  const style: CSSProperties = centred ? { width: centred.width, height: centred.height, objectFit: 'fill' } : { width: '100%', height: '100%', objectFit: fit }
  const src = media ? api.assetURL(media.assetId) : ''
  return (
    <div className="signage-stage" data-shape={shape} ref={ref}>
      <div className="signage-stage__frame" style={{ width: width * scale, height: height * scale }}>
        {media?.kind === 'video' ? (
          <video
            key={media.id}
            ref={film}
            src={src}
            poster={media.posterAssetId ? api.assetURL(media.posterAssetId) : undefined}
            style={style}
            muted
            loop
            playsInline
            onLoadedMetadata={(e) => setNatural({ width: e.currentTarget.videoWidth, height: e.currentTarget.videoHeight })}
          />
        ) : media ? (
          <img key={media.id} src={src} alt="" style={style} onLoad={(e) => setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })} />
        ) : (
          <span className="signage-stage__empty">
            <Icon name="play" size={22} />
            <span>{say('signage.noItems')}</span>
          </span>
        )}
      </div>
    </div>
  )
}

/** The whole list as the hall TV plays it: the TV's own player, its timing and its fit. Esc closes. */
function PlayAll({ entries, library, shape, onClose }: { entries: PlaylistEntry[]; library: SignageMedia[]; shape: StageShape; onClose: () => void }) {
  const { say } = useStaffText()
  const { width, height } = stageCanvas(shape)
  const { ref, scale } = useCanvasScale(width, height)
  const close = useRef<HTMLButtonElement | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const closing = useRef(onClose)
  closing.current = onClose
  const element: LayoutElement = { id: 'play', kind: 'media', x: 0, y: 0, w: width, h: height, z: 0, media: { items: playItems(entries, library) } }
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    close.current?.focus()
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closing.current()
      if (e.key === 'Tab' && box.current) {
        const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
        const to = trapStep(items, document.activeElement instanceof HTMLElement ? document.activeElement : null, e.shiftKey)
        if (to) {
          e.preventDefault()
          to.focus()
        }
      }
    }
    window.addEventListener('keydown', on)
    return () => {
      window.removeEventListener('keydown', on)
      opener?.focus()
    }
  }, [])
  return (
    <div ref={box} className="signage-play" role="dialog" aria-modal="true" aria-label={say('signage.fullScreen')}>
      <Button ref={close} isIconOnly variant="ghost" className="signage-play__close" aria-label={say('signage.close')} onPress={onClose}>
        <Icon name="close" size={20} />
      </Button>
      <div className="layout-fit signage-play__fit" ref={ref}>
        <div className="layout-frame" style={{ width: width * scale, height: height * scale }}>
          <div className="layout-canvas signage-play__canvas" style={{ width, height, transform: `scale(${scale})` }}>
            <MediaPlayer element={element} assetURL={api.assetURL} />
          </div>
        </div>
      </div>
    </div>
  )
}
